import { Component, Input, OnDestroy, OnInit , signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, forkJoin, lastValueFrom, map, Observable, Subscription } from 'rxjs';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { WellTypeService } from '../../services/well-type-datums.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { ConfirmationService, MenuItem, PrimeIcons } from 'primeng/api';
import { FluidType } from 'src/app/perical/models/fluid.model';
import { DialogService } from 'primeng/dynamicdialog';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { GetMudHydrostaticPressure } from 'src/app/perivis/shared/helpers/mud-hydrostatic.helper';
import { unitsLib } from 'src/app/core/services/unit-library';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { _isNumberValue } from '@angular/cdk/coercion';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';

@Component({
  selector: 'app-annulus-contents',
  templateUrl: './annulus-contents.component.html',
  styles: [],
  providers: [DialogService, ConfirmationService],
  standalone: false
})
export class AnnulusContentsComponent implements OnInit, OnDestroy {
  public annulusContentsForm: UntypedFormGroup;
  public columnDefs: any[];
  public tableHeight: string = '600px';
  public isLoading = signal<boolean>(false);
  public _annulusContents: any[];
  public tocDepth: number;
  public fluids: { label: string; value: string, disabled?: boolean }[];
  public userRoles: UserRoles;
  public uu: UserUnitsModel;
  public tocLabel: string;
  public configMissing: boolean;
  public stringFullyCemented: boolean;
  public contextMenuItems: MenuItem[];
  public selectedRowIdx: number;
  public pressureUnit: string;
  public pressureValidation: { min: number, max: number };
  public disabledMessage: string;
  public tubularCemented: boolean;
  public isOutermostString: boolean;
  public seawaterHydrostaticAtHanger: string;
  public depthUnit: string;

  private _defaultAnnulusContent: any;
  private _subscriptions: Subscription = new Subscription();
  private _mudlineDepth: number;
  private _tubular: any;
  private _defaultFluids: { label: string; value: string }[];

  get annulusContents(): UntypedFormArray {
    return this.annulusContentsForm.get('annulusContents') as UntypedFormArray;
  }

  get hangerPressureControl(): UntypedFormControl {
    return this.annulusContentsForm.get('hangerPressure') as UntypedFormControl;
  }

  get isDialogVisible(): boolean {
    return this.configMissing || this.stringFullyCemented;
  }

  @Input()
  private componentId: string;

  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  constructor(
    private _fluidsService: FluidsService,
    private _wellConfigService: WellConfigService,
    private _storeService: StoreService,
    private _signalRService: SignalRService,
    private _wellTypeService: WellTypeService,
    private _confirmationService: ConfirmationService,
    private _formBuilder: UntypedFormBuilder,
    private _trajectoryService: TrajectoryService,
    private _messenger: MediatorService,
  ) {
    this.isLoading.set(true);

    this.annulusContentsForm = this._formBuilder.group({
      annulusContents: this._formBuilder.array([]),
      hangerPressure: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0)] }),
      naturalConvection: new UntypedFormControl(true),
      radiation: new UntypedFormControl(true)
    });

    this._defaultFluids = [
      { label: 'Oxygen', value: 'Oxygen-82fa-4186-a3c3-a09a1ec8b453' },
      { label: 'Methane', value: 'Methane-0f34-4c84-9736-50a6b1a59b1a' },
      { label: 'Carbon Dioxide', value: 'CO2-e84e-4d73-82ca-00eedc18795d' },
      { label: 'Nitrogen', value: 'Nitrogen-139e-4dbb-a350-8733be710f6c' },
      { label: 'Air', value: 'Air-d88d-47d6-81aa-d16e1c55e9fe' },
    ];
  }

  async ngOnInit(): Promise<void> {
    this.uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.userRoles = await this._storeService.get<UserRoles>(StorageKeys.ROLES);

    this.pressureUnit = unitsLib[this.uu.pressure].symbol;
    this.depthUnit = this.uu.longLengths;

    this.columnDefs = [
      { field: 'annulusSectionBottom', header: `Bottom (${this.depthUnit}) MD` },
      { field: 'annulusSectionContent', header: 'Annular Fluid' },
      { field: 'naturalConvection', header: 'Natural Convection' },
      { field: 'radiation', header: 'Radiation' }
    ];

    this.contextMenuItems = [
      { label: 'Insert section above', icon: PrimeIcons.ARROW_UP, command: () => this.onAddAnnulusContent(this.selectedRowIdx) }
    ];

    this._subscriptions.add(this.annulusContentsForm.get('hangerPressure')?.valueChanges.subscribe((value) => {
      if (!this.annulusContentsForm.controls.hangerPressure.pristine) {
        this.handleSave({ hangerPressure: value });
      }
    }));

    this.getData();

    switch (this.uu.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 100000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 6894 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 689475 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 6804 };
        break;
    }

    this.annulusContentsForm.controls.hangerPressure.setValidators([
      Validators.min(this.pressureValidation.min),
      Validators.max(this.pressureValidation.max),
      Validators.required
    ]);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, (d) => this.signalRfunc(d));
  }

  signalRfunc(data: { action: string }) {
    if (data.action === PeriforOnChangeMessages.REFRESH_FLUIDS) {
      this.getData(true);
    } else if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS ||
      data.action === PeriforOnChangeMessages.REFRESH_WELL_CONFIG) {
      this._wellConfigService.invalidateSelectedTubularData();
      this.getData();
    }
  }

  public onAddAnnulusContent(index?: number): void {
    if (this.annulusContents.invalid) {
      return;
    }
    const idx = index ?? 0;
    this.annulusContents.insert(idx, this.newAnnulusContent(true, idx));
  }

  private async getData(isFluidSignalR?: boolean): Promise<void> {
    this._wellConfigService.getTubular().subscribe({
      next: async (tubular) => {
        this._tubular = tubular;
        if (!tubular) {
          this.configMissing = true;
          this.disabledMessage = "Well Configuration Missing: Please specify at least one string";
          this.annulusContentsForm.reset();
          this.annulusContentsForm.disable({ emitEvent: false });
          this.isLoading.set(false);
          return;
        }

        if (!isFluidSignalR) {
          this.annulusContentsForm.reset();
          this.annulusContentsForm.controls.hangerPressure.enable();
          this.disabledMessage = '';
          this.configMissing = false;
        }

        const sources: Observable<any>[] = [
          this._wellConfigService.getAnnulusContents(null) as Observable<any>,
          this._fluidsService.getFluids() as Observable<any>,
          this._wellTypeService.getWellType() as Observable<any>
        ];

    forkJoin(sources)
      .pipe(
        map(async ([annulusContents, fluids, wellType]) => {
          this.tocDepth = tubular.hasCement ? tubular.topOfCementMd : tubular.shoeMd;
          this.tocLabel = `(TOC = ${this.tocDepth.toFixed(2)} ${this.uu.longLengths}MD)`;
          this.fluids = fluids
            .filter(
              (f) =>
                f.state.type === FluidType.BRINE ||
                f.state.type === FluidType.STANDARDMUD ||
                f.state.type === FluidType.ADVANCEDMUD
            )
            .sort((a, b) => a.state.name.localeCompare(b.state.name))
            .map((f) => ({ label: f.state.name, value: f.id }));

          this.fluids.unshift({ label: '----Custom Fluids----', value: '', disabled: true });

          this._defaultFluids.forEach((fluid) => {
            this.fluids.unshift({ label: fluid.label, value: fluid.value });
          });

          if (isFluidSignalR) {
            return;
          }

          if (wellType.type !== 'Land') {
            const waterDepthTvd = await lastValueFrom(this._trajectoryService.getTvdsFromMds([wellType.waterDepth]));
            this.seawaterHydrostaticAtHanger = 'Seawater Hydrostatic: ' + GetMudHydrostaticPressure(waterDepthTvd[0], GetValueFromPpg(8.6, unitsLib[this.uu.density].symbol), this.uu, true).toFixed(2);
          } else if (wellType.drillFloorElevation > wellType.wellheadDepth) {
            this.seawaterHydrostaticAtHanger = '0';
          }

          this.isOutermostString = tubular.sequenceNumber == 0;
          this.stringFullyCemented = tubular.hasCement && Math.abs(tubular.topOfCementMd - tubular.hangerMd) < 0.00001;
          this.tubularCemented = tubular.hasCement && tubular.topOfCementMd < tubular.shoeMd;
          if (this.stringFullyCemented) {
            this.disabledMessage = 'String is fully cemented';
            this.annulusContentsForm.reset();
            this.annulusContentsForm.disable({ emitEvent: false });
            this.tocLabel = '';
            const hangerTvd = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this._tubular.hangerMd]));
            const defaultPressure = GetMudHydrostaticPressure(hangerTvd[0], this._tubular.annularFluid.state.nominalDensity, this.uu, true, true);
            if (annulusContents.annulusContents?.length > 0 || annulusContents.hangerPressure != defaultPressure) {
              this._wellConfigService.updateAnnulusContents({ annulusContents: [], hangerPressure: defaultPressure }, null).subscribe();
              this._messenger.publish(new DataSavedMessage('Annulus Contents'));
            }
            this.annulusContentsForm.patchValue({ hangerPressure: defaultPressure.toFixed(2), annulusContents: [] }, { emitEvent: false });
            this.annulusContents.clear();
            this.isLoading.set(false);
            return;
          }

          if (annulusContents.annulusContents && annulusContents.annulusContents.length == 1 &&
            annulusContents.annulusContents[annulusContents.annulusContents.length - 1]?.annulusSectionBottom > this.tocDepth) {

            let difference = 0;
            let previousBottom: number | null = null;

            [...annulusContents.annulusContents].reverse().forEach(ac => {
              if (ac.annulusSectionBottom != this.tocDepth && difference === 0) {
                difference = ac.annulusSectionBottom - this.tocDepth;
                ac.annulusSectionBottom = this.tocDepth;
                previousBottom = this.tocDepth;
                return;
              }

              if (difference > 0 && previousBottom !== null && ac.annulusSectionBottom > previousBottom) {
                ac.annulusSectionBottom -= difference;
                previousBottom = ac.annulusSectionBottom;
                return;
              }

              previousBottom = ac.annulusSectionBottom;
            });

            this.annulusContentsForm.patchValue({ annulusContents: annulusContents.annulusContents, hangerPressure: annulusContents.hangerPressure }, { emitEvent: false });
            this.handleSave(annulusContents, this.annulusContentsForm, false);
          }

          const waterDepth = wellType.waterDepth ? wellType.waterDepth : 0;
          this._mudlineDepth = wellType.drillFloorElevation + waterDepth;
          
          this._defaultAnnulusContent = this.fluids.find((f) => f.value === this._tubular.annularFluidId)?.value ?? fluids[0].value;
          this._annulusContents = annulusContents?.annulusContents ?? [];

          const hangerPressure = annulusContents.hangerPressure ?? 0;
          if (!this.annulusContentsForm.get('hangerPressure').value)
          {
            this.annulusContentsForm.patchValue({ hangerPressure });
          }

          this.populateFormData();

          if (annulusContents.annulusContents[annulusContents.annulusContents.length - 1]?.annulusSectionBottom < this.tocDepth) {
            annulusContents.annulusContents[annulusContents.annulusContents.length - 1].annulusSectionBottom = this.tocDepth;
            annulusContents.hangerPressure = this.annulusContentsForm.get('hangerPressure').value;
            this.annulusContents.setValue(annulusContents.annulusContents);
            this.handleSave(annulusContents, this.annulusContentsForm, false);
          }
          
          this.isLoading.set(false);
        }),
        catchError((err) => {
          this.configMissing = true;
          this.isLoading.set(false);
          return err;
        })
      )
      .subscribe();
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  private async populateFormData() {
    this.annulusContents.clear();
    if (!this.annulusContentsForm.controls.hangerPressure || this._annulusContents?.length === 0) {
      const defaultAnnulusContent = {
        annulusSectionContent: this._defaultAnnulusContent,
        annulusSectionBottom: this.tocDepth,
        naturalConvection: true,
        radiation: true
      };

      const hangerTvd = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this._tubular.hangerMd]));
      const defaultPressure = GetMudHydrostaticPressure(hangerTvd[0], this._tubular.annularFluid.state.nominalDensity, this.uu, true, true);
      
      this._annulusContents = [defaultAnnulusContent];
      
      const data = {
        annulusContents: this._annulusContents,
        hangerPressure: defaultPressure
      };

      this._wellConfigService.updateAnnulusContents(data, null).subscribe(() => {
        this.annulusContentsForm.patchValue({
          hangerPressure: defaultPressure.toFixed(2),
          annulusContents: this._annulusContents
        });

        this.annulusContentsForm.reset();
        this.annulusContents.clear();
        this.getData();
        return;
      });
    }

    this._annulusContents.forEach((data, idx) => {
      const annulusSectionFg = this.newAnnulusContent(false, idx);
      this.annulusContents.push(annulusSectionFg);
      const expandedData = { ...data };
      const annulusSection = [
        {
          annulusSectionBottom: expandedData.annulusSectionBottom,
          annulusSectionContent: expandedData.annulusSectionContent,
          naturalConvection: expandedData.naturalConvection,
          radiation: expandedData.radiation
        }
      ];
      this.annulusContents.controls[idx].setValue(annulusSection[0], { emitEvent: false });
    });
  }

  public onDeleteSection(idx: number): void {
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete this annulus section?',
      accept: () => {
        this.annulusContents.removeAt(idx);
        this.handleSave(this.annulusContents.value, this.annulusContents.getRawValue(), true);
        this._messenger.publish(new DataSavedMessage('Annulus Contents'));
      }
    });
  }

  public getRowIndex(row: any): void {
    this.selectedRowIdx = row;
  }

  public onFluidChange(annulusSectionFg: UntypedFormGroup, fluid: any): void {
    annulusSectionFg.controls.annulusSectionContent.patchValue(fluid);
    this.handleSave(annulusSectionFg.value, annulusSectionFg);
  }

  private newAnnulusContent(isNewAnnulusContent: boolean, idx: number): UntypedFormGroup {
    const depthFactor = this.uu.longLengths == 'ft' ? 100 : 30;
    let defaultDepth = this.annulusContents.getRawValue()[idx]?.annulusSectionBottom - depthFactor || this.tocDepth;
    if (defaultDepth < this._mudlineDepth) {
      defaultDepth = this._mudlineDepth + 1;
    }
    const annulusSectionFg = new UntypedFormGroup({
      annulusSectionBottom: new UntypedFormControl(
        {
          value: defaultDepth,
          disabled: this.userRoles.readOnly || (!isNewAnnulusContent && idx === this._annulusContents.length - 1),
        },
        {
          validators: [Validators.min(this._mudlineDepth), Validators.required],
          updateOn: 'blur'
        }
      ),
      annulusSectionContent: new UntypedFormControl(
        {
          value: isNewAnnulusContent ? null : this._defaultAnnulusContent,
          disabled: this.userRoles.readOnly
        },
        Validators.required
      ),
      naturalConvection: new UntypedFormControl(
        {
          value: true,
          disabled: this.userRoles.readOnly
        },
        Validators.required
      ),
      radiation: new UntypedFormControl(
        {
          value: true,
          disabled: this.userRoles.readOnly
        },
        Validators.required
      )
    });

    this._subscriptions.add(annulusSectionFg.valueChanges.subscribe((v) => this.handleSave(v, annulusSectionFg)));

    if (isNewAnnulusContent && this.annulusContents.value.length < 1) {
      annulusSectionFg.controls.annulusSectionBottom.patchValue(this.tocDepth, { emitEvent: false });
    }

    annulusSectionFg.markAllAsTouched();

    return annulusSectionFg;
  }

  public async hangerPressureChange(): Promise<void> {
    const hangerTvd = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this._tubular.hangerMd]));
    const defaultPressure = GetMudHydrostaticPressure(hangerTvd[0], this._tubular.annularFluid.state.nominalDensity, this.uu, true, true);
    if (this.annulusContentsForm.get('hangerPressure')?.value == null) {
      this.annulusContentsForm.get('hangerPressure')?.patchValue(defaultPressure.toFixed(2));
    }
  }

  private async handleSave(v: any, annulusContentsRecord?: any, isDelete?: boolean): Promise<void> {
    if (!isDelete && !_isNumberValue(v.hangerPressure) && (v.annulusSectionBottom < this._mudlineDepth || v.annulusSectionBottom > this.tocDepth || !annulusContentsRecord.value?.annulusSectionContent)) {
      return;
    }
    if (annulusContentsRecord?.valid || this.annulusContentsForm.get('hangerPressure')?.valid) {
      const data = {
        annulusContents: [...(v.annulusContents || this.annulusContents.getRawValue())],
        hangerPressure: this.annulusContentsForm.get('hangerPressure')?.value
      };
      await lastValueFrom(this._wellConfigService.updateAnnulusContents(data, null));
      this._messenger.publish(new DataSavedMessage('Annulus Contents'));
    }
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
